import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { listPlansRequest } from "../../api/plansApi";
import { listMarkersRequest } from "../../api/markersApi";
import { usePlanStore } from "../../store/planStore";
import type { Marker, Plan } from "../../types/domain.types";
import { Spinner } from "../UI/Spinner";

function extractErr(e: unknown, fallback: string): string {
  if (isAxiosError(e)) {
    return (
      (e.response?.data as { error?: { message?: string } } | undefined)?.error
        ?.message ?? fallback
    );
  }
  return fallback;
}

export function HistoryList() {
  const { plans, setPlans } = usePlanStore();
  const [planId, setPlanId] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar planes si aún no están en store
  useEffect(() => {
    if (plans.length > 0) {
      if (!planId) setPlanId(plans[0]!.id);
      return;
    }
    let active = true;
    listPlansRequest()
      .then((list) => {
        if (!active) return;
        setPlans(list);
        if (list[0]) setPlanId(list[0].id);
      })
      .catch((e) => {
        if (active) setError(extractErr(e, "Error al cargar planos"));
      });
    return () => {
      active = false;
    };
  }, [plans, planId, setPlans]);

  const load = useCallback(() => {
    if (!planId) return;
    let active = true;
    setLoading(true);
    setError(null);
    listMarkersRequest({
      planId,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(to).toISOString() : undefined,
    })
      .then((list) => {
        if (active) setMarkers(list);
      })
      .catch((e) => {
        if (active) setError(extractErr(e, "Error al cargar marcadores"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [planId, from, to]);

  useEffect(() => {
    return load();
  }, [load]);

  const planById = useMemo(() => {
    const m = new Map<string, Plan>();
    for (const p of plans) m.set(p.id, p);
    return m;
  }, [plans]);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-100">Historial</h1>
        <Link
          to="/plans"
          className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          Volver
        </Link>
      </header>

      <div className="grid grid-cols-1 gap-3 rounded-md bg-slate-900/60 p-3 ring-1 ring-slate-800 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Plano
          <select
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            className="rounded-md bg-slate-800 px-2 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Desde
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md bg-slate-800 px-2 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          Hasta
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md bg-slate-800 px-2 py-2 text-sm text-slate-100 ring-1 ring-slate-700"
          />
        </label>
      </div>

      {loading && (
        <div className="flex justify-center py-6">
          <Spinner size={28} />
        </div>
      )}

      {error && (
        <p role="alert" className="rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {markers.map((m) => (
          <li
            key={m.id}
            className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900 p-3 text-sm text-slate-200"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-brand-light">
                  {m.qrCode}
                </span>
                <span className="text-xs text-slate-500">
                  {new Date(m.createdAt).toLocaleString()}
                </span>
              </div>
              {m.comment && (
                <p className="mt-1 truncate text-xs text-slate-400">
                  {m.comment}
                </p>
              )}
              <p className="text-xs text-slate-500">por {m.user.email}</p>
            </div>
            <Link
              to={`/plans/${planId}`}
              className="ml-3 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
            >
              Ver en plano
            </Link>
          </li>
        ))}
        {!loading && markers.length === 0 && !error && (
          <li className="text-xs text-slate-500">
            Sin marcadores para los filtros actuales.
          </li>
        )}
      </ul>

      <p className="text-xs text-slate-500">
        {planId && planById.get(planId)
          ? `Plano: ${planById.get(planId)!.clientName}`
          : ""}
      </p>
    </div>
  );
}
